import React, { useState, useRef, useEffect } from 'react';
import { Mail, Send, Loader2, ChevronDown, ArrowLeft } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLang } from '../components/LanguageProvider';
import translations from '../translations';

/* ─── Custom Premium Dropdown ─── */
function PremiumSelect({ value, onChange, placeholder, options, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('touchstart', handleClick); };
  }, []);

  const selectedLabel = value || placeholder;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        className={`w-full h-12 px-4 rounded-xl text-left text-sm flex items-center justify-between transition-all duration-200
          dark:bg-white/[0.04] bg-white/80
          dark:border-white/10 border-slate-200 border
          dark:text-white/90 text-slate-700
          dark:hover:border-cyan-500/40 hover:border-cyan-400/60
          dark:focus:border-cyan-500/50 focus:border-cyan-500
          focus:outline-none focus:ring-2 focus:ring-cyan-500/20
          backdrop-blur-sm
          ${!value ? 'dark:text-white/40 text-slate-400' : ''}
          ${open ? 'dark:border-cyan-500/50 border-cyan-400 ring-2 ring-cyan-500/20' : ''}
        `}
      >
        <span className="truncate">{selectedLabel}</span>
        <ChevronDown className={`w-4 h-4 dark:text-white/40 text-slate-400 transition-transform duration-200 shrink-0 ml-2 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-2 z-50 rounded-xl overflow-hidden
          dark:bg-slate-900/95 bg-white/95
          dark:border-white/10 border-slate-200 border
          backdrop-blur-xl
          shadow-[0_8px_30px_rgba(0,0,0,0.3)]
          max-h-[240px] overflow-y-auto"
        >
          {options.map((opt, i) => (
            <button
              key={i}
              type="button"
              disabled={opt.disabled}
              onClick={() => { if (!opt.disabled) { onChange(opt.value); setOpen(false); } }}
              className={`w-full px-4 py-3 text-left text-sm transition-all duration-150
                ${opt.disabled
                  ? 'dark:text-white/20 text-slate-300 cursor-not-allowed'
                  : value === opt.value
                    ? 'dark:bg-cyan-500/15 bg-cyan-50 dark:text-cyan-400 text-cyan-600 font-medium'
                    : 'dark:text-white/70 text-slate-600 dark:hover:bg-white/[0.06] hover:bg-slate-50 dark:hover:text-white hover:text-slate-900'
                }
              `}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Premium Input ─── */
function PremiumInput({ type = 'text', placeholder, value, onChange, className = '' }) {
  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className={`w-full h-12 px-4 rounded-xl text-sm transition-all duration-200
        dark:bg-white/[0.04] bg-white/80
        dark:border-white/10 border-slate-200 border
        dark:text-white text-slate-900
        dark:placeholder-white/30 placeholder-slate-400
        dark:hover:border-cyan-500/40 hover:border-cyan-400/60
        dark:focus:border-cyan-500/50 focus:border-cyan-500
        focus:outline-none focus:ring-2 focus:ring-cyan-500/20
        backdrop-blur-sm
        ${className}`}
    />
  );
}

/* ─── Premium Textarea ─── */
function PremiumTextarea({ placeholder, value, onChange, rows = 6 }) {
  return (
    <textarea
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      rows={rows}
      className="w-full px-4 py-3 rounded-xl text-sm transition-all duration-200 resize-none
        dark:bg-white/[0.04] bg-white/80
        dark:border-white/10 border-slate-200 border
        dark:text-white text-slate-900
        dark:placeholder-white/30 placeholder-slate-400
        dark:hover:border-cyan-500/40 hover:border-cyan-400/60
        dark:focus:border-cyan-500/50 focus:border-cyan-500
        focus:outline-none focus:ring-2 focus:ring-cyan-500/20
        backdrop-blur-sm"
    />
  );
}

/* ─── Form gönderim ucu ───
   ÖLÇÜM (2026-09-08): `src/` içinde bu dosyadaki tek dış çağrı bu.
   Yani form sahte değil, gerçekten gönderiliyor: üçüncü taraf bir form
   iletim hizmeti alınan mesajı destek e-posta kutusuna düşürüyor.
   O yüzden mailto akışına çevrilmedi — çalışan bir şeyi bozmak olurdu.
   Bunun yerine iki şey eklendi:
     · formun altına "nereye gidiyor" notu ve Gizlilik Politikası bağlantısı,
     · gönderim başarısız olursa doğrudan support@whowsell.com yedeği.
   Gizlilik metninde de bu aktarım "form iletim hizmeti sağlayıcısı"
   olarak, sağlayıcı adı verilmeden açıklandı. */
const FORM_UCU_ID = 'mojppqvb';

/* ─── Client-side rate limiting ─── */
const RATE_LIMIT_WINDOW = 60000;
const RATE_LIMIT_MAX_PER_EMAIL = 3;
const RATE_LIMIT_MAX_GLOBAL = 5;
const submissionLog = [];

function isRateLimited(email) {
  const now = Date.now();
  const cutoff = now - RATE_LIMIT_WINDOW;
  while (submissionLog.length > 0 && submissionLog[0].time < cutoff) {
    submissionLog.shift();
  }
  const globalCount = submissionLog.length;
  if (globalCount >= RATE_LIMIT_MAX_GLOBAL) return true;
  const emailNorm = email.trim().toLowerCase();
  const emailCount = submissionLog.filter(e => e.email === emailNorm).length;
  if (emailCount >= RATE_LIMIT_MAX_PER_EMAIL) return true;
  return false;
}

function recordSubmission(email) {
  submissionLog.push({ email: email.trim().toLowerCase(), time: Date.now() });
}

/* ─── Premium Toast notification ─── */
function ToastNotification({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-24 right-6 z-[100] flex items-center gap-3 px-6 py-4 rounded-2xl text-sm font-semibold shadow-2xl backdrop-blur-xl border transition-all duration-500 animate-fade-in
      ${type === 'error'
        ? 'dark:bg-red-950/80 bg-red-50 dark:border-red-500/40 border-red-300 dark:text-red-300 text-red-600'
        : 'dark:bg-emerald-950/80 bg-emerald-50 dark:border-emerald-500/40 border-emerald-300 dark:text-emerald-300 text-emerald-600'
      }`}
      style={{
        boxShadow: type === 'error'
          ? '0 8px 32px rgba(239, 68, 68, 0.2), 0 0 0 1px rgba(239, 68, 68, 0.1)'
          : '0 8px 32px rgba(16, 185, 129, 0.2), 0 0 0 1px rgba(16, 185, 129, 0.1)',
      }}
    >
      <span className={`w-8 h-8 rounded-xl flex items-center justify-center text-white text-base ${type === 'error' ? 'bg-red-500' : 'bg-emerald-500'}`}>
        {type === 'error' ? '✕' : '✓'}
      </span>
      <span>{message}</span>
    </div>
  );
}

/* ─── Main ContactPage ─── */
export default function ContactPage() {
  const { lang } = useLang();
  const t = translations[lang].contact;
  const ortak = translations[lang].common;
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [honeypot, setHoneypot] = useState('');

  const initialDemoRef = useRef(searchParams.get('demo') === 'enterprise');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: initialDemoRef.current ? t.demoSubject : '',
    message: initialDemoRef.current ? t.demoMessage : '',
  });

  useEffect(() => {
    if (!initialDemoRef.current) return;
    initialDemoRef.current = false;
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('demo');
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const validateForm = () => {
    if (!formData.name.trim()) { showToast(t.form.validation.nameRequired, 'error'); return false; }
    if (!formData.email.trim()) { showToast(t.form.validation.emailRequired, 'error'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { showToast(t.form.validation.emailInvalid, 'error'); return false; }
    if (!formData.phone.trim()) { showToast(t.form.validation.phoneRequired, 'error'); return false; }
    if (!formData.subject) { showToast(t.form.validation.subjectRequired, 'error'); return false; }
    if (!formData.message.trim()) { showToast(t.form.validation.messageRequired, 'error'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (honeypot) return;
    if (!validateForm()) return;
    if (isRateLimited(formData.email)) {
      showToast(t.form.rateLimited, 'error');
      return;
    }
    setLoading(true);

    try {
      recordSubmission(formData.email);
      const response = await fetch(`https://formspree.io/f/${FORM_UCU_ID}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          _replyto: formData.email,
          subject: formData.subject,
          message: formData.message,
          _gotcha: honeypot,
        }),
      });

      if (response.ok) {
        showToast(t.form.sent);
        setFormData({ name: '', phone: '', email: '', subject: '', message: '' });
      } else {
        /* Sağlayıcının ham hata metnini kullanıcıya BASMIYORUZ: altyapı
           adı ya da alan adı sızdırabilir. Kendi metnimizi gösteriyoruz. */
        showToast(t.form.failed, 'error');
      }
    } catch (error) {
      showToast(t.form.network, 'error');
    } finally {
      setLoading(false);
    }
  };

  const subjectOptions = t.subjects.slice(1).map(s => ({ value: s, label: s, disabled: false }));

  return (
    <div className="min-h-screen dark:bg-slate-950 bg-[#f0f4f8] dark:text-white text-slate-900 pt-20">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      {/* Toast */}
      {toast && <ToastNotification message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        {/* Back link */}
        <Link to="/" className="inline-flex items-center gap-2 text-sm dark:text-white/50 text-slate-500 hover:dark:text-white hover:text-slate-900 transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" />
          {ortak.home}
        </Link>

        {/* Header */}
        <div className="text-center mb-14">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 animated-gradient-text">
            {t.title}
          </h1>
          <p className="text-base dark:text-white/50 text-slate-500 max-w-lg mx-auto">{t.subtitle}</p>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">

          {/* Form Card */}
          <div className="lg:col-span-3">
            <div className="glow-card rounded-2xl p-6 sm:p-8" style={{ '--glow-color': 'rgba(6, 182, 212, 0.3)' }}>
              <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true" tabIndex={-1}>
                  <input type="text" name="_gotcha" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" />
                </div>
                {/* Name + Phone row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium dark:text-white/80 text-slate-700">{t.form.name}</label>
                    <PremiumInput
                      placeholder={t.form.namePlaceholder}
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium dark:text-white/80 text-slate-700">{t.form.phone}</label>
                    <PremiumInput
                      placeholder={t.form.phonePlaceholder}
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="text-sm font-medium dark:text-white/80 text-slate-700">{t.form.email}</label>
                  <PremiumInput
                    type="email"
                    placeholder={t.form.emailPlaceholder}
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                  />
                </div>

                {/* Subject - Custom Dropdown */}
                <div className="space-y-2">
                  <label className="text-sm font-medium dark:text-white/80 text-slate-700">{t.form.subject}</label>
                  <PremiumSelect
                    value={formData.subject}
                    onChange={(val) => handleChange('subject', val)}
                    placeholder={t.form.subjectPlaceholder}
                    options={subjectOptions}
                  />
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <label className="text-sm font-medium dark:text-white/80 text-slate-700">{t.form.message}</label>
                  <PremiumTextarea
                    placeholder={t.form.messagePlaceholder}
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                  />
                </div>

                {/* Gönder düğmesi.
                    Boyut `.wh-cta`dan gelir (tek satıra kilitli, clamp ile
                    ölçeklenir); eskiden buradaki sabit `h-12 text-sm`
                    ölçüsü sitenin geri kalanındaki düğmelerle uyumsuzdu.
                    `w-full` inline-flex kutuyu satır boyuna açar, içerik
                    yine ortalanmış kalır. */}
                <button
                  type="submit"
                  disabled={loading}
                  className="wh-cta w-full text-slate-950 transition-all duration-300 active:scale-[0.97] hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                  style={{
                    background: 'linear-gradient(135deg, #06b6d4, #22d3ee)',
                    boxShadow: '0 0 0 1px rgba(6, 182, 212, 0.35), 0 8px 24px rgba(6, 182, 212, 0.25)',
                  }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" />
                      {t.form.sending}
                    </>
                  ) : (
                    <>
                      {t.form.send}
                      <Send />
                    </>
                  )}
                </button>

                <p className="text-center text-xs dark:text-white/30 text-slate-400">{t.availability}</p>

                {/* DÜRÜSTLÜK NOTU — form gerçekten gönderiliyor ve nereye
                    gittiği burada yazıyor; verinin işlenmesi Gizlilik
                    Politikası'na bağlanmış durumda. */}
                <p className="text-center text-[11px] leading-relaxed dark:text-white/30 text-slate-400 break-words">
                  {t.formNote}{' '}
                  <Link to="/privacy" className="underline underline-offset-2 hover:dark:text-cyan-400 hover:text-cyan-600 transition-colors">
                    {t.formNoteLink}
                  </Link>
                  {t.formNoteAfter}
                </p>
                <p className="text-center text-[11px] leading-relaxed dark:text-white/25 text-slate-400 break-words">
                  {t.mailtoFallback}
                </p>
              </form>
            </div>
          </div>

          {/* Direct Contact Card */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <a
              href="mailto:support@whowsell.com"
              className="block group"
            >
              <div className="glow-card rounded-2xl p-8 flex flex-col items-center text-center gap-5 h-full transition-all duration-300 group-hover:scale-[1.02]"
                style={{ '--glow-color': 'rgba(139, 92, 246, 0.3)' }}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                  style={{
                    background: 'linear-gradient(135deg, #06b6d4, #8b5cf6, #ec4899)',
                    boxShadow: '0 4px 20px rgba(6, 182, 212, 0.3)',
                  }}>
                  <Mail className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold dark:text-white text-slate-900 mb-1">{t.directContact.email.title}</h3>
                  <p className="text-sm dark:text-white/40 text-slate-500 mb-3">{t.directContact.email.subtitle}</p>
                  <p className="text-sm font-medium text-cyan-500 break-all">{t.directContact.email.value}</p>
                </div>
              </div>
            </a>

            {/* DÖRT ÜRÜNÜN PANELLERİ — güncel adlar ve güncel alan adları.
                Eskiden bu kutuda "Rol bazlı güvenli erişim" ve "7/24
                destek" yazıyordu: birincisi ölü rol modelinden kalmıştı
                (yetki artık mağaza bazlı), ikincisi de hemen üstündeki
                "24 saat içinde yanıt" cümlesiyle çelişiyordu. */}
            <div className="glow-card rounded-2xl p-6" style={{ '--glow-color': 'rgba(139, 92, 246, 0.2)' }}>
              <h3 className="text-sm font-bold dark:text-white text-slate-900 mb-3">{t.productsTitle}</h3>
              <ul className="space-y-2.5">
                {translations[lang].about.products.map((urun, i) => (
                  <li key={i}>
                    <a
                      href={urun.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-2.5 text-sm dark:text-white/60 text-slate-600 hover:dark:text-cyan-400 hover:text-cyan-600 transition-colors break-words"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 shrink-0 mt-1.5" />
                      {urun.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Info cards */}
            <div className="glow-card rounded-2xl p-6" style={{ '--glow-color': 'rgba(6, 182, 212, 0.2)' }}>
              <h3 className="text-sm font-bold dark:text-white text-slate-900 mb-3">{t.whyTitle}</h3>
              <ul className="space-y-2.5">
                {t.why.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm dark:text-white/60 text-slate-600 break-words">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 shrink-0 mt-1.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
